import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  MailPlus,
  Mail,
  Users,
  Hospital,
  MessagesSquare,
  Folder,
  BookOpenText,
  Settings,
  LogOut,
  Calendar1Icon,
  BadgeDollarSign,
} from "lucide-react";
import { Calendar, Menu } from "antd";
import { useEffect, useMemo, useState } from "react";
import { clearSession } from "../utils/authSession";

export default function AdminSidebar() {
  const location = useLocation();
  const path = location.pathname;
  const navigate = useNavigate();

  // Admin-only submenu keys
  const rootSubmenuKeys = useMemo(() => ["sr", "cm", "pr"], []);
  const [openKeys, setOpenKeys] = useState([]);

  const getSelectedKey = () => {
    if (path.includes("/admin/dashboard")) return "dashboard";
    if (path.includes("/admin/services")) return "services";
    if (path.includes("/admin/requests")) return "requests";
    if (path.includes("/admin/lawyers")) return "lawyers";
    if (path.includes("/admin/firms")) return "firms";
    if (path.includes("/admin/chats")) return "chats";
    if (path.includes("/admin/meetings")) return "meetings";
    if (path.includes("/admin/blogs")) return "blogs";
    if (path.includes("/admin/caseStudies")) return "caseStudies";
    if (path.includes("/admin/category")) return "category";
    if (path.includes("/admin/logos")) return "logos";
    if (path.includes("/admin/faqs")) return "faqs";
    if (path.includes("/admin/social")) return "social";
    if (path.includes("/admin/testimonials")) return "testimonials";
    if (path.includes("/admin/queries")) return "queries";
    if (path.includes("/admin/newsletter")) return "newsletter";
    if (path.includes("/admin/packages")) return "packages";
    if (path.includes("/admin/subscriptions")) return "subscriptions";
    if (path.includes("/admin/profile")) return "settings";
    return "";
  };

  const handleLogout = () => {
    clearSession();
    navigate("/login");
  };

  useEffect(() => {
    const keys = [];
    if (path.includes("/admin/services") || path.includes("/admin/requests")) {
      keys.push("sr");
    }
    if (path.includes("/admin/blogs") || path.includes("/admin/caseStudies") || path.includes("/admin/category") || path.includes("/admin/logos") || path.includes("/admin/faqs") || path.includes("/admin/social") || path.includes("/admin/testimonials")) {
      keys.push("cm");
    }
    if (path.includes("/admin/packages") || path.includes("/admin/subscriptions")) {
      keys.push("pr");
    }
    setOpenKeys(keys);
  }, [path]);

  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((k) => !openKeys.includes(k));
    if (!latestOpenKey) return setOpenKeys(keys);
    if (rootSubmenuKeys.includes(latestOpenKey)) setOpenKeys([latestOpenKey]);
    else setOpenKeys(keys);
  };

  // Admin-only SR submenu
  const srChildren = [
    { key: "services", label: <Link to="/admin/services">Services</Link> },
    { key: "requests", label: <Link to="/admin/requests">Service Requests</Link> },
  ];

  // Admin-only menu items
  const items = [
    {
      key: "dashboard",
      icon: <LayoutDashboard size={18} />,
      label: <Link to="/admin/dashboard">Dashboard</Link>,
    },
    {
      key: "sr",
      icon: <MailPlus size={18} />,
      label: "Service Requests",
      children: srChildren,
    },
    {
      key: "lawyers",
      icon: <Users size={18} />,
      label: <Link to="/admin/lawyers">Lawyers</Link>,
    },
    {
      key: "firms",
      icon: <Hospital size={18} />,
      label: <Link to="/admin/firms">Corporate Firms</Link>,
    },
    {
      key: "chats",
      icon: <MessagesSquare size={18} />,
      label: <Link to="/admin/chats">Chats & Messages</Link>,
    },
    {
      key: "meetings",
      icon: <Calendar1Icon size={18} />,
      label: <Link to="/admin/meetings">Meetings</Link>,
    },
    {
      key: "cm",
      icon: <Folder size={18} />,
      label: "Content Management",
      children: [
        { key: "blogs", label: <Link to="/admin/blogs">Blogs</Link> },
        { key: "caseStudies", label: <Link to="/admin/caseStudies">Case Studies</Link> },
        { key: "category", label: <Link to="/admin/category">Category</Link> },
        { key: "logos", label: <Link to="/admin/logos">Partner Logos</Link> },
        { key: "faqs", label: <Link to="/admin/faqs">FAQs</Link> },
        { key: "social", label: <Link to="/admin/social">Social Media</Link> },
        { key: "testimonials", label: <Link to="/admin/testimonials">Testimonials</Link> },
      ],
    },
    {
      key: "pr",
      icon: <BadgeDollarSign size={18} />,
      label: "Pricing",
      children: [
        { key: "packages", label: <Link to="/admin/packages">Packages</Link> },
        { key: "subscriptions", label: <Link to="/admin/subscriptions">Subscriptions</Link> },
      ],
    },
    {
      key: "queries",
      icon: <BookOpenText size={18} />,
      label: <Link to="/admin/queries">Query Forms</Link>,
    },
    {
      key: "newsletter",
      icon: <Mail size={18} />,
      label: <Link to="/admin/newsletter">Newsletter</Link>,
    },
    {
      key: "settings",
      icon: <Settings size={18} />,
      label: <Link to="/admin/profile">Profile</Link>,
    },
    {
      key: 'logout',
      label: (
        <button
          type="button"
          onClick={handleLogout}
          style={{
            font:25,
            color:'white',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <LogOut size={18} />
          <span>Logout</span>
        </button>
      ),
    }

  ];

  return (
    <aside className="admin-sidebar">
      <Menu
        mode="inline"
        theme="dark"
        className="sidebar-menu"
        selectedKeys={[getSelectedKey()]}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        items={items}
      />
    </aside>
  );
}
